import React from "react";
import {
  List,
  TextField,
  Show,
  SimpleShowLayout,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  DateField,
  FilterButton,
  CreateButton,
  TopToolbar,
  SearchInput,
  SelectInput,
  useDataProvider,
  AutocompleteInput,
  SelectColumnsButton,
  DatagridConfigurable,
  SelectField,
  EditButton,
  Button,
  ArrayField,
  Datagrid,
  useRecordContext,
  useNotify,
  NumberField,
  DateInput,
  ArrayInput,
  SimpleFormIterator,
  usePermissions,
  useUpdate,
  useRefresh,
  required,
  useGetIdentity,
} from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import ExportAllButton from "../components/ExportAllButton";
import DynamicDropdownFilter from "../components/DynamicDropdownFilter";
import AddMitigationModal from "../components/AddMitigationModal";
import ReassignRiskModal from "../components/ReassignRiskModal";
import Grid from "@mui/material/Grid";
import SecurityIcon from "@mui/icons-material/Security";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  RISK_LEVEL_CHOICES,
  RISK_LIKELIHOOD_CHOICES,
  RISK_SEVERITY_CHOICES,
  RISK_STATUS_CHOICES,
  RISK_TYPE_CHOICES,
} from "../consts";
import StringArrayField from "../components/StringArrayField";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SeverityChipField from "../components/SeverityChipField";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const riskFilters = (permissions: string[]) => {
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");
  const isRmfAuditor = permissions.includes("rmf_auditor");

  const validStatusFilters =
    isAdmin || isRmfAdmin
      ? RISK_STATUS_CHOICES
      : RISK_STATUS_CHOICES.filter(({ id }) =>
          ["open", "closed", "new"].includes(id)
        );

  const filters = [
    <SearchInput source="q_name_description_department_division" alwaysOn />,
    <DynamicDropdownFilter label="Name" source="name" resource="rmf/risks" />,
    <DynamicDropdownFilter
      label="Creator"
      source="createdBy"
      resource="rmf/risks"
    />,
    <DynamicDropdownFilter
      label="Assignee"
      source="assignee"
      resource="rmf/risks"
    />,
    <DynamicDropdownFilter
      label="Executive Owner"
      source="executiveOwner"
      resource="rmf/risks"
    />,
    <DynamicDropdownFilter
      label="Division"
      source="division"
      resource="rmf/risks"
    />,
    <DynamicDropdownFilter
      label="Department"
      source="department"
      resource="rmf/risks"
    />,
    <DynamicDropdownFilter
      label="Categories"
      source="categories"
      resource="rmf/risks"
    />,
    <DynamicDropdownFilter label="Tags" source="tags" resource="rmf/risks" />,
    <SelectInput source="severity" choices={RISK_SEVERITY_CHOICES} />,
    <SelectInput source="likelihood" choices={RISK_LIKELIHOOD_CHOICES} />,
    <SelectInput source="riskType" choices={RISK_TYPE_CHOICES} />,
    <SelectInput source="riskLevel" choices={RISK_LEVEL_CHOICES} />,
    <SelectInput source="mitigatedRiskLevel" choices={RISK_LEVEL_CHOICES} />,
  ];
  if (!isRmfAuditor) {
    filters.push(
      <SelectInput
        source="expirationDate"
        label="Expiration Filter"
        choices={[
          { id: "expired", name: "Expired" },
          { id: "expiring_soon", name: "Expiring Soon (7d)" },
          { id: "not_expired", name: "Not Expired" },
        ]}
        format={(value) => {
          if (value?.$lte) {
            return "expired";
          }
          if (value?.$lt) {
            return "expiring_soon";
          }
          if (value?.$gt) {
            return "not_expired";
          }
          return "";
        }}
        parse={(value) => {
          switch (value) {
            case "expired":
              return { $gt: "", $lt: "", $lte: new Date() };
            case "not_expired":
              return { $gt: new Date(), $lt: "", $lte: "" };
            case "expiring_soon":
              const date = new Date();
              date.setDate(date.getDate() + 7);
              return { $lt: date, $gt: new Date(), $lte: "" };
            default:
              return "";
          }
        }}
      />
    );
    filters.push(<SelectInput source="status" choices={validStatusFilters} />);
  }
  return filters;
};

const ListActions = () => (
  <TopToolbar>
    <SelectColumnsButton />
    <FilterButton />
    <CreateButton />
    <ExportAllButton />
  </TopToolbar>
);

export const RiskList = () => {
  const { permissions } = usePermissions();
  return (
    <List
      filters={riskFilters(permissions)}
      perPage={25}
      actions={<ListActions />}
    >
      <DatagridConfigurable
        rowClick="show"
        omit={[
          "mitigatedRiskLevel",
          "executiveOwner",
          "department",
          "division",
        ]}
      >
        <TextField source="name" />
        <TextField source="division" />
        <TextField source="department" />
        <TextField source="assignee" />
        <TextField source="executiveOwner" />
        <SelectField source="status" choices={RISK_STATUS_CHOICES} />
        <SelectField source="riskType" choices={RISK_TYPE_CHOICES} />
        <SeverityChipField source="likelihood" resource="rmf/risks" />
        <SeverityChipField source="severity" resource="rmf/risks" />
        <SeverityChipField source="riskLevel" resource="rmf/risks" />
        <SeverityChipField source="mitigatedRiskLevel" resource="rmf/risks" />
        <DateField source="expirationDate" />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
      </DatagridConfigurable>
    </List>
  );
};

const ApproveButton = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");

  const handleClick = () => {
    // Ensure a record exists and it's not already approved before attempting an update
    if (record && !record.is_approved) {
      update(
        "rmf/risks", // resource
        { id: record.id, data: { status: "open" }, previousData: record },
        {
          onSuccess: () => {
            notify("Risk ticket approved successfully");
            refresh(); // Refresh the page to show the updated status
          },
          onError: (error) => {
            notify(`Error approving risk ticket: ${error.message}`, {
              type: "error",
            });
          },
        }
      );
    }
  };

  if (!record || !(isAdmin || isRmfAdmin)) return null;

  return (
    <Button
      label="Approve"
      onClick={handleClick}
      disabled={!["new", "rejected"].includes(record.status) || isLoading}
    >
      <CheckIcon />
    </Button>
  );
};

const RejectButton = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");

  const handleClick = () => {
    // Ensure a record exists and it's not already approved before attempting an update
    if (record && !record.is_approved) {
      update(
        "rmf/risks", // resource
        { id: record.id, data: { status: "rejected" }, previousData: record },
        {
          onSuccess: () => {
            notify("Risk ticket rejected successfully");
            refresh(); // Refresh the page to show the updated status
          },
          onError: (error) => {
            notify(`Error rejecting risk ticket: ${error.message}`, {
              type: "error",
            });
          },
        }
      );
    }
  };

  if (!record || !(isAdmin || isRmfAdmin)) return null;
  return (
    <Button
      label="Reject"
      onClick={handleClick}
      disabled={!["new", "open"].includes(record.status) || isLoading}
    >
      <ClearIcon />
    </Button>
  );
};

const ReopenButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = React.useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");
  const [open, setOpen] = React.useState(false);

  if (!record || !(isAdmin || isRmfAdmin)) return null;

  const handleSubmit = async (values: any) => {
    if (!record || record.is_approved) return;

    const {
      division,
      department,
      assignee,
      executiveOwner,
      newExpirationDate: expirationDate,
    } = values;

    setIsLoading(true);
    try {
      await dataProvider.custom(`rmf/risks/${record.id}/reopen`, "PATCH", {
        division,
        department,
        assignee,
        executiveOwner,
        expirationDate,
      });
      notify("Risk ticket reopened successfully");
      setOpen(false);
      refresh();
    } catch (error: any) {
      notify(
        `Error reopening risk ticket: ${error.message || "Unknown error"}`,
        {
          type: "error",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const disabled =
    !["closed", "archived"].includes(record.status) || isLoading || !record;

  return (
    <>
      <Button label="Reopen" onClick={() => setOpen(true)} disabled={disabled}>
        <AutorenewIcon />
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reopen Risk Ticket</DialogTitle>
        <DialogContent>
          <SimpleForm
            id="reopen-risk-form"
            defaultValues={{
              division: record.division || "",
              department: record.department || "",
              assignee: record.assignee || "",
              executiveOwner: record.executiveOwner || "",
              newExpirationDate: new Date(
                Date.now() + 90 * 24 * 60 * 60 * 1000
              ),
            }}
            onSubmit={handleSubmit}
            toolbar={false}
          >
            <ExecutiveOwnerField />
            <FilteredDivisionField />
            <FilteredDepartmentField />
            <FilteredAssigneeField />
            <DateInput
              source="newExpirationDate"
              label="Expiration Date (defaults to 90 days)"
            />
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setOpen(false)} />
          <Button
            label="Save"
            onClick={() => {
              const form = document.getElementById(
                "reopen-risk-form"
              ) as HTMLFormElement | null;
              if (form) {
                form.requestSubmit();
              }
            }}
            disabled={isLoading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

const MitigateButton = () => {
  const [mitigationModalOpen, setMitigationModalOpen] = React.useState(false);
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const { data } = useGetIdentity();
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");
  if (!record || (!(isAdmin || isRmfAdmin) && record.assignee !== data?.email))
    return null;
  return (
    <>
      <Button
        label="Mitigate"
        onClick={() => setMitigationModalOpen(true)}
        disabled={
          mitigationModalOpen || !record?.status || record?.status !== "open"
        }
      >
        <SecurityIcon />
      </Button>
      <AddMitigationModal
        open={mitigationModalOpen}
        onClose={() => setMitigationModalOpen(false)}
      />
    </>
  );
};

const ReassignButton = () => {
  const [reassignModalOpen, setReassignModalOpen] = React.useState(false);
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const isRmfEditor = permissions.includes("rmf_editor");
  const { data } = useGetIdentity();
  // Don't render if lacking permissions or current user is not record.executiveOwner
  if (!record || !isRmfEditor || record.executiveOwner !== data?.email)
    return null;
  return (
    <>
      <Button
        label="Reassign"
        onClick={() => setReassignModalOpen(true)}
        disabled={reassignModalOpen || !["new", "open"].includes(record.status)}
      >
        <SwapHorizIcon />
      </Button>
      <ReassignRiskModal
        open={reassignModalOpen}
        onClose={() => setReassignModalOpen(false)}
      />
    </>
  );
};

const CloneButton = () => {
  const record = useRecordContext();
  return (
    <CreateButton
      state={{
        record: {
          ...record,
          _id: undefined,
          mitigations: [],
          mitigatedLikelihood: null,
          mitigatedRiskLevel: null,
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          openedAt: null,
          openedBy: null,
          closedAt: null,
          closedBy: null,
          status: "open",
        },
      }}
      label="Clone"
      icon={<ContentCopyIcon />}
    />
  );
};

const ShowActions = () => {
  return (
    <TopToolbar>
      <ApproveButton />
      <RejectButton />
      <ReopenButton />
      <ReassignButton />
      <MitigateButton />
      <CloneButton />
      <EditButton />
    </TopToolbar>
  );
};

const DownloadArtifactButton = ({ mitigationId }: { mitigationId: string }) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { id: riskId } = useParams(); // Get risk ID from URL
  const [loading, setLoading] = React.useState(false);

  const handleDownload = async () => {
    if (!record?.id || !record.filename || !riskId || !mitigationId) {
      notify("Unable to download file: Missing required parameters", {
        type: "error",
      });
      return;
    }
    setLoading(true);
    try {
      // Use dataProvider to make the API call
      const response = await dataProvider.custom(
        `rmf/risks/${riskId}/mitigations/${mitigationId}/artifacts/${record.id}/downloadUrl`,
        "GET"
      );
      const signedUrl = response?.data;

      if (signedUrl) {
        // Create a temporary link and trigger download without redirecting
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = record.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error: any) {
      notify(error.message || "Failed to download file", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label=""
      onClick={handleDownload}
      disabled={loading}
      sx={{
        minWidth: "auto",
        "& .MuiButton-startIcon": {
          marginLeft: 0,
          marginRight: 0,
        },
      }}
    >
      <DownloadIcon />
    </Button>
  );
};

const Artifact = () => {
  const record = useRecordContext();
  return (
    <ArrayField source="artifact">
      <Datagrid
        bulkActionButtons={false}
        sx={{
          paddingLeft: "3em",
          maxWidth: "40%",
          backgroundColor: "tertiary",
          borderRadius: "0 0 5px 5px",
        }}
        isRowSelectable={() => false}
        isRowExpandable={() => false}
        rowClick={false}
      >
        <TextField source="filename" sx={{ color: "secondary" }} />
        {record?.id && (
          <Box
            sx={{
              textAlign: "right",
            }}
          >
            <DownloadArtifactButton mitigationId={record.id.toString()} />
          </Box>
        )}
      </Datagrid>
    </ArrayField>
  );
};
const Aside = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");
  const showAdminFields = isAdmin || isRmfAdmin;
  return (
    <div style={{ width: 250, margin: "1em" }}>
      {record && (
        <Grid container spacing={3}>
          <SimpleShowLayout>
            <SelectField source="status" choices={RISK_STATUS_CHOICES} />
            <TextField source="division" />
            <TextField source="department" />
            <TextField source="executiveOwner" />
            <TextField source="assignee" />
            {record?.expirationDate && <DateField source="expirationDate" />}
          </SimpleShowLayout>
          <SimpleShowLayout>
            {showAdminFields && <TextField source="createdBy" />}
            <DateField source="createdAt" />
            {showAdminFields && record.openedBy && (
              <TextField source="openedBy" />
            )}
            {record.openedAt && <DateField source="openedAt" />}
            {showAdminFields && <TextField source="updatedBy" />}
            {showAdminFields && <DateField source="updatedAt" />}
            {showAdminFields && record.closedBy && (
              <TextField source="closedBy" />
            )}
            {record.closedAt && <DateField source="closedAt" />}
          </SimpleShowLayout>
        </Grid>
      )}
    </div>
  );
};
const MitigatedDisplayField = () => {
  const record = useRecordContext();
  return (
    record?.mitigatedRiskLevel && (
      <Grid>
        <SimpleShowLayout>
          <SeverityChipField
            source="mitigatedLikelihood"
            resource="rmf/risks"
          />
          <SeverityChipField source="mitigatedRiskLevel" resource="rmf/risks" />
        </SimpleShowLayout>
      </Grid>
    )
  );
};
export const RiskShow = () => (
  <Show aside={<Aside />} actions={<ShowActions />}>
    <SimpleShowLayout sx={{ maxWidth: 1600, margin: "auto" }}>
      <TextField source="name" />
      <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
      <Grid container spacing={3}>
        <Grid>
          <SimpleShowLayout>
            <SelectField source="riskType" choices={RISK_TYPE_CHOICES} />
            <SeverityChipField source="severity" resource="rmf/risks" />
          </SimpleShowLayout>
        </Grid>
        <Grid>
          <SimpleShowLayout>
            <SeverityChipField source="likelihood" resource="rmf/risks" />
            <SeverityChipField source="riskLevel" resource="rmf/risks" />
          </SimpleShowLayout>
        </Grid>
        <MitigatedDisplayField />
      </Grid>
      <ArrayField source="mitigations">
        <Datagrid
          bulkActionButtons={false}
          expand={<Artifact />}
          rowClick="expand"
          isRowExpandable={(record) => record.artifact.length > 0}
          empty={
            <Typography
              sx={{
                padding: "0",
                fontSize: "0.875rem",
                marginTop: "0.2em",
              }}
            >
              No mitigation steps available
            </Typography>
          }
        >
          <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
          <NumberField source="artifact.length" label="Artifacts" />
          <TextField source="addedAt" />
          <TextField source="addedBy" />
        </Datagrid>
      </ArrayField>
      <StringArrayField source="categories" />
      <StringArrayField source="tags" />
    </SimpleShowLayout>
  </Show>
);

export const RiskEdit = () => {
  return (
    <Edit redirect="show" mutationMode="pessimistic">
      <SimpleForm>
        <TextInput source="name" />
        <TextInput source="description" multiline rows={4} />
        <ExecutiveOwnerField />
        <FilteredDivisionField />
        <FilteredDepartmentField />
        <FilteredAssigneeField />
        <SelectInput source="status" choices={RISK_STATUS_CHOICES} />
        <SelectInput source="riskType" choices={RISK_TYPE_CHOICES} />
        <SelectInput source="severity" choices={RISK_SEVERITY_CHOICES} />
        <SelectInput source="likelihood" choices={RISK_LIKELIHOOD_CHOICES} />
        <CalculatedRiskLevelField
          likelihoodSource="likelihood"
          targetSource="riskLevel"
          label="Risk Level"
          helperText="Auto-calculated based on severity and likelihood"
        />
        <SelectInput
          source="mitigatedLikelihood"
          choices={RISK_LIKELIHOOD_CHOICES}
        />
        <CalculatedRiskLevelField
          likelihoodSource="mitigatedLikelihood"
          targetSource="mitigatedRiskLevel"
          label="Mitigated Risk Level"
          helperText="Auto-calculated based on severity and mitigated likelihood"
        />
        <ArrayInput source="mitigations">
          <SimpleFormIterator disableAdd disableReordering>
            <TextInput source="description" />
            <TextInput source="addedBy" />
            <TextInput source="addedAt" />
            <ArrayInput source="artifact">
              <SimpleFormIterator disableAdd disableReordering>
                <TextInput source="filename" />
                <TextInput source="filepath" />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
        <DynamicDropdownFilter
          allowTextInput
          label="Categories"
          source="categories"
          resource="rmf/risks"
        />
        <DynamicDropdownFilter
          allowTextInput
          label="Tags"
          source="tags"
          resource="rmf/risks"
        />
        <DateInput source="expirationDate" />
      </SimpleForm>
    </Edit>
  );
};

const FilteredDivisionField = () => {
  const department = useWatch({ name: "department" });
  const assignee = useWatch({ name: "assignee" });
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const [divisions, setDivisions] = React.useState<
    { id: string; name: string }[]
  >([]);
  const prevDepartmentRef = React.useRef<string | undefined>(undefined);
  const prevAssigneeRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const fetchAssignees = async () => {
      if (
        (prevDepartmentRef.current &&
          prevDepartmentRef.current !== department &&
          department) ||
        (prevAssigneeRef.current &&
          prevAssigneeRef.current !== assignee &&
          assignee)
      ) {
        setValue("division", "");
      }
      prevDepartmentRef.current = department;
      prevAssigneeRef.current = assignee;
      const filter: any = {};
      if (department) {
        filter.department = department;
      }
      if (assignee) {
        filter.email = assignee;
      }

      try {
        const response = await dataProvider.getUnique("rmf/employees", {
          field: "division",
          filter,
        });

        setDivisions(
          response.map((value: string) => ({
            id: value,
            name: value,
          }))
        );
        if (response.length === 1) {
          setValue("division", response[0]);
        }
      } catch (error) {
        setDivisions([]);
      }
    };

    fetchAssignees();
  }, [assignee, department, dataProvider, setValue]);

  return (
    <AutocompleteInput
      label="Division"
      source="division"
      choices={divisions.map((choice, index) => ({
        ...choice,
        key: `${choice.id}-${index}`,
      }))}
    />
  );
};

const FilteredDepartmentField = () => {
  const division = useWatch({ name: "division" });
  const assignee = useWatch({ name: "assignee" });
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const [departments, setDepartments] = React.useState<
    { id: string; name: string }[]
  >([]);
  const prevDivisionRef = React.useRef<string | undefined>(undefined);
  const prevAssigneeRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const fetchAssignees = async () => {
      if (
        (prevDivisionRef.current &&
          prevDivisionRef.current !== division &&
          division) ||
        (prevAssigneeRef.current &&
          prevAssigneeRef.current !== assignee &&
          assignee)
      ) {
        setValue("department", "");
      }

      prevDivisionRef.current = division;
      prevAssigneeRef.current = assignee;
      const filter: any = {};
      if (division) {
        filter.division = division;
      }
      if (assignee) {
        filter.email = assignee;
      }

      try {
        const response = await dataProvider.getUnique("rmf/employees", {
          field: "department",
          filter,
        });

        setDepartments(
          response.map((value: string) => ({
            id: value,
            name: value,
          }))
        );
        if (response.length === 1) {
          setValue("department", response[0]);
        }
      } catch (error) {
        setDepartments([]);
      }
    };

    fetchAssignees();
  }, [assignee, division, dataProvider, setValue]);

  return (
    <AutocompleteInput
      label="Department"
      source="department"
      choices={departments.map((choice, index) => ({
        ...choice,
        key: `${choice.id}-${index}`,
      }))}
    />
  );
};

const FilteredAssigneeField = () => {
  const division = useWatch({ name: "division" });
  const department = useWatch({ name: "department" });
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const [assignees, setAssignees] = React.useState<
    { id: string; name: string }[]
  >([]);
  const prevDivisionRef = React.useRef<string | undefined>(undefined);
  const prevDepartmentRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const fetchAssignees = async () => {
      if (
        (prevDivisionRef.current &&
          prevDivisionRef.current !== division &&
          division) ||
        (prevDepartmentRef.current &&
          prevDepartmentRef.current !== department &&
          department)
      ) {
        setValue("assignee", "");
      }
      prevDivisionRef.current = division;
      prevDepartmentRef.current = department;
      const filter: any = {};
      if (division) {
        filter.division = division;
      }
      if (department) {
        filter.department = department;
      }

      try {
        const response = await dataProvider.getUnique("rmf/employees", {
          field: "email",
          filter,
        });

        setAssignees(
          response.map((value: string) => ({
            id: value,
            name: value,
          }))
        );
      } catch (error) {
        setAssignees([]);
      }
    };

    fetchAssignees();
  }, [department, division, dataProvider, setValue]);

  return (
    <AutocompleteInput
      label="Assignee"
      source="assignee"
      choices={assignees.map((choice, index) => ({
        ...choice,
        key: `${choice.id}-${index}`,
      }))}
    />
  );
};

const ExecutiveOwnerField = () => {
  const division = useWatch({ name: "division" });
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const [, setExecutiveOwner] = React.useState<string>("");

  React.useEffect(() => {
    const fetchExecutiveOwner = async () => {
      if (!division) {
        setExecutiveOwner("");
        setValue("executiveOwner", "");
        return;
      }

      try {
        const response = await dataProvider.getList("rmf/employees", {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "name", order: "ASC" },
          filter: { division, executiveOwner: true },
        });

        if (response.data && response.data.length > 0) {
          const ownerName = response.data[0].name || "";
          setExecutiveOwner(ownerName);
          setValue("executiveOwner", ownerName);
        } else {
          setExecutiveOwner("");
          setValue("executiveOwner", "");
        }
      } catch (error) {
        console.error("Error fetching executive owner:", error);
        setExecutiveOwner("");
        setValue("executiveOwner", "");
      }
    };

    fetchExecutiveOwner();
  }, [division, dataProvider, setValue]);

  return (
    <TextInput
      source="executiveOwner"
      label="Executive Owner"
      disabled
      helperText="Auto-populated based on selected division"
    />
  );
};

// Risk level calculation logic matching backend
const calculateRiskLevel = (
  severity: string | undefined,
  likelihood: string | undefined
): string => {
  if (!severity || !likelihood) return "";

  if (severity === "catastrophic") {
    if (["frequent", "likely"].includes(likelihood)) return "extremely_high";
    if (["occasional", "seldom"].includes(likelihood)) return "high";
    return "medium";
  } else if (severity === "critical") {
    if (likelihood === "frequent") return "extremely_high";
    if (["likely", "occasional"].includes(likelihood)) return "high";
    if (likelihood === "seldom") return "medium";
  } else if (severity === "moderate") {
    if (likelihood === "frequent") return "high";
    if (["likely", "occasional"].includes(likelihood)) return "medium";
  } else if (severity === "negligible") {
    if (likelihood === "frequent") return "medium";
  }
  return "low";
};

interface CalculatedRiskLevelFieldProps {
  severitySource?: string;
  likelihoodSource: string;
  targetSource: string;
  label: string;
  helperText: string;
}

const CalculatedRiskLevelField = ({
  severitySource = "severity",
  likelihoodSource,
  targetSource,
  label,
  helperText,
}: CalculatedRiskLevelFieldProps) => {
  const severity = useWatch({ name: severitySource });
  const likelihood = useWatch({ name: likelihoodSource });
  const { setValue } = useFormContext();

  const riskLevel = React.useMemo(
    () => calculateRiskLevel(severity, likelihood),
    [severity, likelihood]
  );

  React.useEffect(() => {
    setValue(targetSource, riskLevel);
  }, [riskLevel, setValue, targetSource]);

  return (
    <SelectInput
      source={targetSource}
      label={label}
      choices={RISK_LEVEL_CHOICES}
      disabled
      helperText={helperText}
    />
  );
};

export const RiskCreate = () => {
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes("admin");
  const isRmfAdmin = permissions.includes("rmf_admin");
  const defaultStatus = isAdmin || isRmfAdmin ? "open" : "new";
  const disableStatus = !isAdmin;
  // Get current date + 90 days
  const currentDate = new Date();
  const expirationDateDefault = new Date(
    currentDate.getTime() + 90 * 24 * 60 * 60 * 1000
  );
  return (
    <Create redirect={"show"}>
      <SimpleForm>
        <TextInput source="name" />
        <TextInput source="description" multiline rows={4} />
        <ExecutiveOwnerField />
        <FilteredDivisionField />
        <FilteredDepartmentField />
        <FilteredAssigneeField />
        <SelectInput
          source="riskType"
          choices={RISK_TYPE_CHOICES}
          validate={required()}
        />
        <SelectInput
          source="severity"
          choices={RISK_SEVERITY_CHOICES}
          validate={required()}
        />
        <SelectInput
          source="likelihood"
          choices={RISK_LIKELIHOOD_CHOICES}
          validate={required()}
        />
        <CalculatedRiskLevelField
          likelihoodSource="likelihood"
          targetSource="riskLevel"
          label="Risk Level"
          helperText="Auto-calculated based on severity and likelihood"
        />
        {isAdmin && (
          <>
            <SelectInput
              source="mitigatedLikelihood"
              choices={RISK_LIKELIHOOD_CHOICES}
            />
            <CalculatedRiskLevelField
              likelihoodSource="mitigatedLikelihood"
              targetSource="mitigatedRiskLevel"
              label="Mitigated Risk Level"
              helperText="Auto-calculated based on severity and mitigated likelihood"
            />
          </>
        )}
        <SelectInput
          source="status"
          choices={RISK_STATUS_CHOICES}
          defaultValue={defaultStatus}
          disabled={disableStatus}
        />
        <DynamicDropdownFilter
          allowTextInput
          label="Categories"
          source="categories"
          resource="rmf/risks"
        />
        <DynamicDropdownFilter
          allowTextInput
          label="Tags"
          source="tags"
          resource="rmf/risks"
        />
        {(isAdmin || isRmfAdmin) && (
          <DateInput
            source="expirationDate"
            label="Expiration Date (defaults to 90 days)"
            defaultValue={expirationDateDefault}
          />
        )}
      </SimpleForm>
    </Create>
  );
};
